import {Emitter} from '@wonderlandengine/api';
import {NotifyPropertyChanged} from '../utils/NotifyPropertyChanged.js';
import {Enemy} from './Enemy.js';
import {AllQuests, Quest} from './Quest.js';
import {Weapon} from './combat/Weapon.js';
import {CharacterStats} from './combat/characterStats.js';
import {FruitData, FruitTypes, allPowerFruit} from '../components/power-fruit.js';
import {gameState} from './GameState.js';
import {Constants} from './data/constants.js';
import {addPlayerAudio} from '../utils/Audio.js';
import {getLevelForXP, getXPForKill, getXPForLevel} from './combat/experience.js';
import {extra} from '../utils/uber-sdk/extra.js';
import {saveGame} from '../utils/uber-sdk/savegame.js';
import {vec3} from 'gl-matrix';
import {ProgressionStatus, ResourceFlowType} from '../utils/game-analytics-enums.js';

import type {GameAnalytics as GA} from 'gameanalytics';
import {messageConsole} from '../components/message-controller.js';
declare var GameAnalytics: typeof GA;

export enum PlayerStates {
    Init,
    Idle,
    Move,
    Hit,
    Attack,
    Dying,
    Dead,
    Dashing,
    Power,
}

/**
 * Player class
 */
export class Player extends NotifyPropertyChanged implements CharacterStats {
    name: string;
    level: number = 1;
    weapon: Weapon | null = null;
    powerFruit: FruitTypes | null = null;
    xp: number = 0;

    health: number = 0;
    maxHealth: number = 0;
    maxHealthStat: number = 1;

    energy: number = 0;
    maxEnergy: number = 0;
    maxEnergyStat: number = 1;

    isInShop: boolean = false;
    currentRegion: string = '';

    melee: number = 1;
    sword: number = 1;

    onAttack: Emitter = new Emitter();
    onHit: Emitter<[number]> = new Emitter();
    onDie: Emitter = new Emitter();
    onRespawn: Emitter = new Emitter();
    onRevive: Emitter = new Emitter();

    position?: vec3;

    private _coins = 0;
    private _quests: Quest[] = [];
    /* Time the quest was last completed */
    private _completedQuests: Record<string, number> = {};

    private _state: PlayerStates = PlayerStates.Init;
    private _lastAttackTime: number = 0;
    private _lastState = PlayerStates.Init;
    private _currentState = PlayerStates.Init;

    get state() {
        return this._state;
    }

    get displayXp() {
        return `${this.xp} / ${getXPForLevel(this.level + 1)}`;
    }

    // TODO: Add some sort of inventory system

    constructor(name: string) {
        super();
        this.name = name;

        this.loadPlayerState();

        addPlayerAudio(this);

        window.onunload = () => {
            /* Save player position */
            this.savePlayerState();
        };

        setInterval(() => {
            if (
                this.energy < this.maxEnergy &&
                this.state !== PlayerStates.Dead &&
                this.state !== PlayerStates.Dying
            ) {
                this.energy += this.maxEnergy / Constants.energyFullSteps;
                if (this.energy > this.maxEnergy) {
                    this.energy = this.maxEnergy;
                }
                this.notifyPropertyChanged('energy');
            }
        }, Constants.energyRegenI);
    }

    /**
     * gets the current number of Coins the player has
     */
    get coins(): number {
        return this._coins;
    }

    get currentQuest(): Quest | null {
        return this._quests.length == 0 ? null : this._quests[0];
    }

    get dead() {
        return this.health == 0;
    }

    addFruit(type: FruitTypes) {
        this.powerFruit = type;
        this.savePlayerState();
    }

    // addMessage(m: string) {
    //     const message = new Message(m);
    //     this.messages.push(message);
    //     if (this.messages.length > 5) {
    //         this.messages.splice(0, 1);
    //     }
    //     this.notifyPropertyChanged('messages');
    //     return message.id;
    // }

    /**
     * sets the current quest of the player
     * @param quest - the quest to set as the current quest
     */
    acceptQuest(quest: Quest): void {
        this._quests.push(quest);
        this.savePlayerState();
    }

    /**
     * Checks if the player already has completed a quest
     * @param quest - the quest to check if the player has completed
     * @returns True if the player has completed the quest
     */
    hasFinishedQuest(quest: Quest): boolean {
        return quest.id in this._completedQuests;
    }

    /** Milliseconds since the quest was last completed */
    timeSinceLastFinishedQuest(quest: Quest): number | null {
        if (quest.id in this._completedQuests) {
            return Date.now() - this._completedQuests[quest.id];
        }
        return null;
    }

    /**
     * attempts to complete the current quest
     * @returns boolean - true if the quest was completed and the reward was given
     */
    tryCompleteQuest(quest: Quest): boolean {
        const index = this._quests.indexOf(quest);
        if (index >= 0 && quest.checkCompleted()) {
            this._completedQuests[quest.id] = Date.now();
            const reward = quest.giveReward();
            this.addCoins(reward.coins);

            GameAnalytics.addResourceEvent(
                ResourceFlowType.Source,
                'coins',
                reward.coins,
                'quest',
                quest.id
            );

            this._quests.splice(index, 1);
            this.savePlayerState();
            return true;
        }
        return false;
    }

    addCoins(coins: number) {
        this._coins += coins;
        this.savePlayerState();
        this.notifyPropertyChanged('coins');
    }

    /**
     * Checks if the player has a quest
     * @param quest - the quest to check if the player has. If no quest is passed in, it will return true if the player has any quest
     * @returns boolean - true if the player has the quest
     */
    hasQuest(quest: Quest): boolean {
        return this._quests.includes(quest);
    }

    /**
     * Player has killed an enemy
     * @param name - the name of the enemy that was killed
     * @param level - the level of the enemy that was killed
     */
    kill(name: string, level: number) {
        this.addXP(getXPForKill(level));

        const l = getXPForLevel(this.level + 1);
        if (this.xp >= l) {
            this._levelUp(getLevelForXP(this.xp));
        }

        for (const q of this._quests) {
            /* FIXME We have a bug where the quest might become undefined */
            if (q) q.action(name);
        }
    }

    /**
     * Levels up the player to the given level
     * @param l The level to level up to
     */
    private _levelUp(l: number) {
        this.level = l;

        //increase stats
        this.melee++;

        this._calculateMax();
        this.health = this.maxHealth;
        this.notifyPropertyChanged('level');
        this.notifyPropertyChanged('xp');
        this.notifyPropertyChanged('health');

        GameAnalytics.addProgressionEvent(
            ProgressionStatus.Complete,
            'player',
            'level',
            this.level.toString()
        );

        extra.celebrate();
    }

    // Method to equip a weapon
    equipWeapon(weapon: Weapon) {
        this.weapon = weapon;
    }

    castFruitPower(): boolean {
        if (!this.powerFruit) {
            return false;
        }
        const fruit = allPowerFruit[this.powerFruit];

        if (!fruit) {
            console.warn('Player has a power fruit but it is not in allPowerFruit');
            return false;
        }

        if (this.energy < fruit.energyUsage) {
            return false;
        }

        this.setState(PlayerStates.Power);

        this.energy -= fruit.energyUsage;
        this.notifyPropertyChanged('energy');
        return true;
    }

    /**
     * Method to attack another character
     * @param target - the character to attack
     * @param withFruit - the fruit to attack with. If null, the player will attack with their melee weapon or bare hands
     * @returns the amount of damage dealt
     */
    attack(target: Enemy | Player, withFruit: FruitData | null = null): number {
        if (gameState.currentPlayer.state === PlayerStates!.Dead) {
            return 0;
        }
        if (!withFruit && this.energy < Constants.attackEnergyCost) {
            this.setState(PlayerStates.Idle);
            return 0;
        }

        this.setState(PlayerStates.Attack);
        let damage = 0;
        if (!withFruit) {
            if (this.weapon) {
                //target.takeDamage(this.weapon.damage + this.strength, this);
                damage =
                    this.level +
                    this.weapon.baseDamage +
                    Math.random() * this.weapon.baseDamage;
            } else {
                damage = this.level + this.melee + Math.random() * this.melee;
            }
            this.energy -= Constants.attackEnergyCost;
            this.notifyPropertyChanged('energy');
        } else {
            if (this.powerFruit !== null) {
                damage = withFruit.damageCalculation(this.level);
                //   this.energy -= withFruit.energyUsage;
                //   this.notifyPropertyChanged('energy');
            } else {
                console.warn('player is trying to attack with fruit but has no fruit');
            }
        }

        this._dealDamage(target, damage);

        return damage;
        //}
    }

    private _dealDamage(target: Enemy | Player, damage: number) {
        if (target.takeDamage(damage, this)) {
            // target is killed
            this.kill(target.name, target.level);
        }
    }

    /**
     * Method to take damage
     */
    takeDamage(amount: number) {
        /* Player is dead, take no further damange */
        if (this.health == 0) {
            return 0;
        }

        let damageTaken = amount;
        if (damageTaken < 0) {
            damageTaken = 0; // Prevents healing from an attack
        }
        this.health = Math.max(0, this.health - damageTaken);
        this.notifyPropertyChanged('health');
        this.onHit.notify(damageTaken);

        if (this.health <= 0) {
            this.setState(PlayerStates.Dying);
        } else {
            this.setState(PlayerStates.Hit);
        }

        return damageTaken;
    }

    /**
     * Method called when the character's health reaches 0
     */
    die() {
        // Handle character death (e.g., remove from game, drop items, etc.)
        console.log(`${this.constructor.name} has died.`);
        this.onDie.notify();

        // analytics event is fired in Enemy.ts to be able to know which enemy got the player!
    }

    respawn() {
        this._calculateMax();

        // reset save with data
        this.savePlayerState();

        this.revive();
        this.onRespawn.notify();

        GameAnalytics.addDesignEvent(`player:respawn`);
    }

    revive() {
        this.setState(PlayerStates.Idle);
        this.health = this.maxHealth;
        this.energy = this.maxEnergy;
        this.notifyPropertyChanged('health');
        this.notifyPropertyChanged('energy');
        this.onRevive.notify();

        GameAnalytics.addDesignEvent(`player:revive`);
    }

    setState(state: PlayerStates) {
        this._currentState = state;
        if (this._state === PlayerStates.Move || this._state === PlayerStates.Idle)
            this._lastState = this._state;

        if (state === PlayerStates.Attack) {
            const now = Date.now();
            // attack is a bit special since we want to be able to hit over and over again.
            // check time since last attack and if it's too soon, don't allow the state change
            // this is to prevent spamming the attack button
            if (now - this._lastAttackTime < Constants.attackCooldown) {
                return;
            }
            this._lastAttackTime = now;
        } else if (this._state === state) {
            return;
        }

        this._state = state;
        this.notifyPropertyChanged('state');

        if (state === PlayerStates.Dead) {
            this.onDie.notify();
            gameState.gameOver();
        }
    }

    attackStateReset() {
        if (this._state === PlayerStates.Move || this._state === PlayerStates.Idle) {
            this._lastState = this._state;
        }

        if (this._state != this._lastState) {
            if (this._currentState != PlayerStates.Dead) {
                this._state = this._lastState;
                this.notifyPropertyChanged('state');
            }
        }
    }

    private _calculateMax() {
        this.maxHealth =
            this.level * Constants.healthPerLevel +
            this.maxHealthStat * Constants.healthPerStat;
        this.maxEnergy =
            this.level * Constants.energyPerLevel +
            this.maxEnergyStat * Constants.energyPerStat;
        this.notifyPropertyChanged('maxHealth');
        this.notifyPropertyChanged('maxEnergy');
    }

    private _setInitialValues() {
        this._quests = [];
        this._completedQuests = {};

        messageConsole.clear();
        this.powerFruit = null;
        this._calculateMax();
        this.health = this.maxHealth;
        this.energy = this.maxEnergy;

        this.notifyPropertyChanged('messages');
        this.notifyPropertyChanged('health');
        this.notifyPropertyChanged('energy');
    }

    /**
     * Saves the player state to a cookie
     */
    savePlayerState() {
        const playerData = {
            xp: this.xp,
            _coins: this._coins,
            _quests: this._quests.filter((q) => !!q).map((q) => q.id),
            _completedQuests: this._completedQuests,
            powerFruit: this.powerFruit,
            health: this.health,
            energy: this.energy,
            weapon: this.weapon,
            position: this.position,
        };
        saveGame.save(playerData);
    }

    /**
     * Loads the player state from a cookie. If there's no state, it sets the initial values
     */
    loadPlayerState() {
        const playerData = saveGame.load();
        if (playerData) {
            const player = playerData;
            this.xp = player.xp;
            this.level = getLevelForXP(this.xp);
            this._coins = player._coins;

            const quests = playerData._quests;
            this._quests = quests
                .filter((q: any) => !!q)
                .map((q: {id: string}) => AllQuests[q.id]);
            this._completedQuests = player._completedQuests;

            this.powerFruit = player.powerFruit;
            this._calculateMax();
            this.health = player.health;
            this.energy = player.energy;
            this.weapon = player.weapon;
            this.position = player.position;

            if (this.position) {
                /* If the player ran way out of bounds, reload should fix it */
                if (
                    Math.abs(this.position[0]) > 800 ||
                    Math.abs(this.position[1]) > 800 ||
                    Math.abs(this.position[1]) > 800
                ) {
                    delete this.position;
                }
            }

            this.notifyPropertyChanged('level');
            this.notifyPropertyChanged('xp');
            this.notifyPropertyChanged('coins');
            this.notifyPropertyChanged('messages');
            this.notifyPropertyChanged('health');
            this.notifyPropertyChanged('energy');
            this.notifyPropertyChanged('weapon');
        } else {
            this._setInitialValues();
        }
    }

    addXP(amount: number): void {
        this.xp += amount;
        this.notifyPropertyChanged('xp');
        this.savePlayerState();
    }

    exitShop() {
        this.isInShop = false;
        this.notifyPropertyChanged('isInShop');
    }

    enterShop() {
        this.isInShop = true;
        this.notifyPropertyChanged('isInShop');
    }

    buyWeapon(item: Weapon): boolean {
        if (this._coins < item.price) {
            messageConsole.addMessage('Not enough coins to buy this item');
            return false;
        }
        this._coins -= item.price;
        this.notifyPropertyChanged('coins');
        this.weapon = item;
        this.notifyPropertyChanged('weapon');
        this.savePlayerState();
        // Make sure animations changes to 1H weapon.
        this.notifyPropertyChanged('state');
        this.exitShop();

        GameAnalytics.addResourceEvent(
            ResourceFlowType.Sink,
            'coins',
            item.price,
            'buy-weapon',
            item.name
        );
        return true;
    }

    enterRegion(regionName: string) {
        this.currentRegion = regionName;
        this.notifyPropertyChanged('currentRegion');
    }

    /**
     * Called when the start method of the player-component is done and the player
     * is ready to use.
     */
    componentReady() {
        if (this.dead) {
            this.die();
            this.setState(PlayerStates.Dying);
        } else {
            this.setState(PlayerStates.Idle);
        }
    }
}
